import { Dispatch, SetStateAction } from "react";
import { useTranslation } from "react-i18next";
import {
  ListItem,
  Typography,
  Button,
  Box,
  ListItemText,
  Chip,
} from "@material-ui/core";
import { ExpandMore } from "@material-ui/icons";
import ConfirmDelete from "components/ConfirmDelete";
import {
  ListItemAccordion,
  StyledSecondaryAction,
  StyledContentList,
  StyledSummary,
  StyledDetails,
} from "components/List/StyledComponents";
import { useDependencyModal } from "components/DependencyModal";
import { getFormattedTimeStamp } from "utils/dateUtils";
import apiRequest from "utils/apiRequestWithErrorCode";
import { SecurityDefinition } from "@dashboard-v3/api";
import storageProviders from "utils/storageProviders";
import { translationKey as providerLabelKey } from "components/StorageProviderLabel";

const ContentItem = ({ wording }: { wording: string }) => {
  return (
    <li>
      <p>{wording}</p>
    </li>
  );
};

interface ItemProps {
  content: SecurityDefinition;
  refreshList: () => Promise<void>;
  setLoading: Dispatch<SetStateAction<"ALL" | "MORE" | "ITEM">>;
  setLoadingItem: Dispatch<SetStateAction<string>>;
  onClickEdit: (id: string) => void;
}

export default function SecurityDefinitionItem({
  content,
  refreshList,
  setLoading,
  setLoadingItem,
  onClickEdit,
}: ItemProps) {
  const { t } = useTranslation(["rules", "securityDefinitions"]);
  const { errorHandler } = useDependencyModal();
  const {
    id,
    name,
    updatedAt,
    expiresInDays,
    access,
    individualPassword,
    systemDefault,
  } = content;

  const getExpirationText = () => {
    return expiresInDays
      ? t("securityDefinitions:expiresInDays.expiration", {
          days: expiresInDays,
        })
      : t("securityDefinitions:expiresInDays.persistent");
  };

  const deleteDefinition = async (id: string) => {
    try {
      setLoading("ITEM");
      setLoadingItem(id);
      await apiRequest<void>("DELETE", `/security-definitions/${id}`);
      await refreshList();
    } catch (e) {
      errorHandler(
        e,
        t(`securityDefinitions:errors.definitionsRequests.deleteError`)
      );
    } finally {
      setLoading(null);
    }
  };

  const renderProviderLabel = () => {
    const { systemDefault, allowedStorages } = content;
    const [provider] = allowedStorages;

    if (systemDefault) {
      const isRestricted = !storageProviders.every(s =>
        allowedStorages.includes(s)
      );

      return isRestricted
        ? allowedStorages
            .map(s => t(providerLabelKey(s)))
            .sort()
            .reduce((acc, provider, i, arr) => {
              return arr.length - 1 === i
                ? acc.concat(" ").concat(t("common:and", { item: provider }))
                : acc.concat(`, ${provider}`);
            })
        : t("securityDefinitions:allCloudServices");
    }

    return t(providerLabelKey(provider));
  };

  return (
    <ListItem disableGutters data-testid={`sd-list-item-${id}`}>
      <ListItemAccordion>
        <StyledSummary
          data-testid={`sd-item-summary-${id}`}
          expandIcon={<ExpandMore />}
        >
          <ListItemText
            primary={
              <>
                <Box marginY="5px">
                  <Typography
                    display="inline"
                    style={{ fontWeight: "500", marginRight: "10px" }}
                  >
                    {t(`securityDefinitions:presets.${id}.name`, name)}
                  </Typography>
                  <Typography
                    color="textSecondary"
                    display="inline"
                    variant="body2"
                  >
                    {renderProviderLabel()}
                  </Typography>
                </Box>
                <Box marginBottom="5px">
                  {systemDefault ? (
                    <Chip
                      size="small"
                      label="Preset"
                      style={{ marginRight: "10px" }}
                    />
                  ) : (
                    <Typography color="textSecondary" variant="body2">
                      {t("securityDefinitions:updatedAt", {
                        date: getFormattedTimeStamp(updatedAt, true),
                      })}
                    </Typography>
                  )}
                </Box>
              </>
            }
          />
          {!systemDefault && (
            <StyledSecondaryAction>
              <Button
                data-testid={`sd-edit-btn-${id}`}
                disableElevation
                variant="outlined"
                size="small"
                onClick={event => {
                  event.stopPropagation();
                  onClickEdit(content.id);
                }}
              >
                {t("common:edit")}
              </Button>
              <ConfirmDelete
                onAccept={() => deleteDefinition(id)}
                message={t("securityDefinitions:confirmDeletion", { name })}
                variant="outlined"
                size="small"
                data-testid={`sd-delete-btn-${id}`}
              />
            </StyledSecondaryAction>
          )}
        </StyledSummary>
        <StyledDetails>
          <StyledContentList>
            <ContentItem wording={t(`securityDefinitions:access.${access}`)} />
            <ContentItem wording={getExpirationText()} />
            {content.individualPassword && (
              <ContentItem
                wording={t(
                  `securityDefinitions:individualPassword.${individualPassword}`
                )}
              />
            )}
            {!content.canDownload && (
              <ContentItem wording={t("securityDefinitions:canDownload")} />
            )}
            {content.removeDownloadLink && (
              <ContentItem wording={t("securityDefinitions:removeLink")} />
            )}
          </StyledContentList>
        </StyledDetails>
      </ListItemAccordion>
    </ListItem>
  );
}
